import { supabase } from '../lib/supabase';

import { toCamelCase } from './supabaseService';

// ============================================
// NOTIFICATIONS SERVICE
// ============================================

/**
 * Get all notifications for the current user
 */
export async function getUserNotifications(filters = {}) {
  try {
    // Dev mode bypass
    const isDevMode = import.meta.env?.VITE_DEV_MODE === 'true';
    let userId;
    
    if (isDevMode) {
      userId = '00000000-0000-0000-0000-000000000001';
    } else {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) throw new Error('User not authenticated');
      userId = user?.id;
    }

    let query = supabase?.from('notifications')?.select('*')?.eq('user_id', userId);

    // Apply filters
    if (filters?.type && filters?.type !== 'all') {
      query = query?.eq('type', filters?.type);
    }

    if (filters?.priority && filters?.priority !== 'all') {
      query = query?.eq('priority', filters?.priority);
    }

    if (filters?.isRead !== undefined && filters?.isRead !== 'all') {
      query = query?.eq('is_read', filters?.isRead === 'read');
    }

    if (filters?.startDate) {
      query = query?.gte('created_at', filters?.startDate);
    }

    if (filters?.endDate) {
      query = query?.lte('created_at', filters?.endDate);
    }

    // Order by created_at descending (newest first)
    query = query?.order('created_at', { ascending: false });

    const { data, error } = await query;

    if (error) throw error;

    return toCamelCase(data || []);
  } catch (error) {
    console.error('Error fetching notifications:', error);
    throw error;
  }
}

/**
 * Get unread notification count
 */
export async function getUnreadCount() {
  try {
    // Dev mode bypass
    const isDevMode = import.meta.env?.VITE_DEV_MODE === 'true';
    let userId;
    
    if (isDevMode) {
      userId = '00000000-0000-0000-0000-000000000001';
    } else {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) throw new Error('User not authenticated');
      userId = user?.id;
    }

    const { count, error } = await supabase?.from('notifications')?.select('*', { count: 'exact', head: true })?.eq('user_id', userId)?.eq('is_read', false);

    if (error) throw error;

    return count || 0;
  } catch (error) {
    console.error('Error fetching unread count:', error);
    return 0;
  }
}

/**
 * Mark notification as read
 */
export async function markAsRead(notificationId) {
  try {
    // Dev mode bypass
    const isDevMode = import.meta.env?.VITE_DEV_MODE === 'true';
    let userId;
    
    if (isDevMode) {
      userId = '00000000-0000-0000-0000-000000000001';
    } else {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) throw new Error('User not authenticated');
      userId = user?.id;
    }

    const { data, error } = await supabase?.from('notifications')?.update({ is_read: true, read_at: new Date()?.toISOString() })?.eq('id', notificationId)?.eq('user_id', userId)?.select();

    if (error) throw error;

    return toCamelCase(data?.[0]);
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
}

/**
 * Mark multiple notifications as read
 */
export async function markMultipleAsRead(notificationIds) {
  try {
    // Dev mode bypass
    const isDevMode = import.meta.env?.VITE_DEV_MODE === 'true';
    let userId;
    
    if (isDevMode) {
      userId = '00000000-0000-0000-0000-000000000001';
    } else {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) throw new Error('User not authenticated');
      userId = user?.id;
    }

    const { data, error } = await supabase?.from('notifications')?.update({ is_read: true, read_at: new Date()?.toISOString() })?.in('id', notificationIds)?.eq('user_id', userId)?.select();

    if (error) throw error;

    return toCamelCase(data || []);
  } catch (error) {
    console.error('Error marking notifications as read:', error);
    throw error;
  }
}

/**
 * Mark all notifications as read
 */
export async function markAllAsRead() {
  try {
    // Dev mode bypass
    const isDevMode = import.meta.env?.VITE_DEV_MODE === 'true';
    let userId;
    
    if (isDevMode) {
      userId = '00000000-0000-0000-0000-000000000001';
    } else {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) throw new Error('User not authenticated');
      userId = user?.id;
    }

    const { data, error } = await supabase?.from('notifications')?.update({ is_read: true, read_at: new Date()?.toISOString() })?.eq('user_id', userId)?.eq('is_read', false)?.select();

    if (error) throw error;

    return toCamelCase(data || []);
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
}

/**
 * Delete notification
 */
export async function deleteNotification(notificationId) {
  try {
    // Dev mode bypass
    const isDevMode = import.meta.env?.VITE_DEV_MODE === 'true';
    let userId;
    
    if (isDevMode) {
      userId = '00000000-0000-0000-0000-000000000001';
    } else {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) throw new Error('User not authenticated');
      userId = user?.id;
    }

    const { error } = await supabase?.from('notifications')?.delete()?.eq('id', notificationId)?.eq('user_id', userId);

    if (error) throw error;

    return true;
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw error;
  }
}

/**
 * Delete multiple notifications
 */
export async function deleteMultipleNotifications(notificationIds) {
  try {
    // Dev mode bypass
    const isDevMode = import.meta.env?.VITE_DEV_MODE === 'true';
    let userId;
    
    if (isDevMode) {
      userId = '00000000-0000-0000-0000-000000000001';
    } else {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) throw new Error('User not authenticated');
      userId = user?.id;
    }

    const { error } = await supabase?.from('notifications')?.delete()?.in('id', notificationIds)?.eq('user_id', userId);

    if (error) throw error;

    return true;
  } catch (error) {
    console.error('Error deleting notifications:', error);
    throw error;
  }
}

/**
 * Subscribe to real-time notification updates
 */
export function subscribeToNotifications(userId, callback) {
  const channel = supabase?.channel(`notifications:${userId}`)?.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`
      },
      (payload) => {
        callback({ type: 'INSERT', notification: toCamelCase(payload?.new) });
      }
    )?.on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`
      },
      (payload) => {
        callback({ type: 'UPDATE', notification: toCamelCase(payload?.new) });
      }
    )?.on(
      'postgres_changes',
      {
        event: 'DELETE',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`
      },
      (payload) => {
        callback({ type: 'DELETE', notificationId: payload?.old?.id });
      }
    )?.subscribe();

  return channel;
}

/**
 * Subscribe to unread count updates
 */
export function subscribeToUnreadCount(userId, callback) {
  const channel = supabase?.channel(`unread_count:${userId}`)?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`
      },
      async () => {
        // Recalculate unread count
        const count = await getUnreadCount();
        callback(count);
      }
    )?.subscribe();

  return channel;
}

/**
 * Unsubscribe from channel
 */
export function unsubscribeChannel(channel) {
  if (channel) {
    supabase?.removeChannel(channel);
  }
}

// Export all functions as a service object
export const notificationsService = {
  getUserNotifications,
  getUnreadCount,
  markAsRead,
  markMultipleAsRead,
  markAllAsRead,
  deleteNotification,
  deleteMultipleNotifications,
  subscribeToNotifications,
  subscribeToUnreadCount,
  unsubscribeChannel
};